import {
  HOODIE_TEAM_PRICING,
  applicableDiscountCode,
  desiredTeamPricingCodes,
  hoodieQuantity,
  quoteTeamPricing,
  teamPricingBridgeCode,
} from 'nwp-team-pricing-model';

const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
  style: 'currency',
  currency: HOODIE_TEAM_PRICING.currency,
});
const root = window.Shopify?.routes?.root || '/';
const state = {
  cart: null,
  queue: Promise.resolve(),
  checkoutRequested: false,
};

function route(path) {
  return `${root}${path}`;
}

async function cartRequest(path, init = {}, timeout = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(route(path), { ...init, signal: controller.signal });
    const body = await response.json().catch(() => null);
    if (!response.ok || !body) throw new Error('team_pricing_cart_failed');
    return body;
  } finally {
    clearTimeout(timer);
  }
}

const discountCodes = (cart) =>
  (cart?.discount_codes || [])
    .map((discount) => String(discount.code || '').trim())
    .filter(Boolean);

const normalizedCodes = (codes) =>
  [...new Set(codes.map((code) => code.toUpperCase()))].sort().join(',');

function price(cents) {
  return money.format(Number(cents || 0) / 100);
}

function nextTierText(quote) {
  if (!quote.nextTier) return 'Best team price unlocked.';
  const needed = quote.nextTier.minimum - quote.quantity;
  return `${needed} more ${needed === 1 ? 'hoodie' : 'hoodies'} to unlock ${price(quote.nextTier.unitCents)} each.`;
}

function cartTotals(cart) {
  const items = (cart?.items || []).filter(
    (item) => String(item.product_id) === HOODIE_TEAM_PRICING.productId,
  );
  if (items.some((item) => !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0))
    throw new Error('team_pricing_cart_failed');
  return {
    original: items.reduce(
      (total, item) => total + Number(item.original_line_price || item.final_line_price || 0),
      0,
    ),
    final: items.reduce((total, item) => total + Number(item.final_line_price || 0), 0),
  };
}

function productEstimateQuantity(element, cart) {
  const formId = element.dataset.form;
  const form = formId ? document.getElementById(formId) : element.closest('product-info')?.querySelector('form');
  const input = form?.querySelector('[name="quantity"]');
  const adding = Math.max(1, Number.parseInt(input?.value || '1', 10) || 1);
  return hoodieQuantity(cart) + adding;
}

function renderElement(element, cart) {
  const cartScope = element.dataset.scope === 'cart';
  const quantity = cartScope ? hoodieQuantity(cart) : productEstimateQuantity(element, cart);
  const quote = quoteTeamPricing(quantity);
  const totals = cartScope ? cartTotals(cart) : null;
  const totalCents = totals ? totals.final : quote.totalCents;
  const originalCents = totals ? totals.original : quote.regularTotalCents;
  const average = quantity ? totalCents / quantity : HOODIE_TEAM_PRICING.basePriceCents;
  element.hidden = cartScope && quantity === 0;
  for (const node of element.querySelectorAll('[data-team-pricing-quantity]'))
    node.textContent = String(quantity);
  for (const node of element.querySelectorAll('[data-team-pricing-unit]'))
    node.textContent = `${price(average)}${quote.bridge ? ' avg.' : ''}`;
  for (const node of element.querySelectorAll('[data-team-pricing-total]'))
    node.textContent = price(totalCents);
  for (const node of element.querySelectorAll('[data-team-pricing-savings]'))
    node.textContent = price(Math.max(0, originalCents - totalCents));
  for (const node of element.querySelectorAll('[data-team-pricing-next]'))
    node.textContent = nextTierText(quote);
}

function render(cart) {
  state.cart = cart;
  for (const element of document.querySelectorAll('nwp-team-pricing')) renderElement(element, cart);
}

if (!customElements.get('nwp-team-pricing')) {
  customElements.define(
    'nwp-team-pricing',
    class extends HTMLElement {
      connectedCallback() {
        if (state.cart) renderElement(this, state.cart);
      }
    },
  );
}

function announceFailure(message = 'We could not verify your team price. Please try Checkout again. Your cart is unchanged.') {
  for (const node of document.querySelectorAll('[data-team-pricing-status]')) {
    node.hidden = false;
    node.textContent = message;
  }
}

async function reconcile(candidate) {
  const before = candidate?.items ? candidate : await cartRequest('cart.js');
  const desired = desiredTeamPricingCodes(before);
  if (normalizedCodes(desired) === normalizedCodes(discountCodes(before))) {
    const appliedBridge = teamPricingBridgeCode(hoodieQuantity(before));
    if (appliedBridge && !applicableDiscountCode(before, appliedBridge) && cartTotals(before).final > quoteTeamPricing(hoodieQuantity(before)).totalCents)
      throw new Error('team_pricing_bridge_rejected');
    render(before);
    return before;
  }
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }),
  );
  const uniqueSections = [...new Set(sections.filter(Boolean))];
  const after = await cartRequest('cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      discount: desired.join(','),
      ...(uniqueSections.length
        ? {
            sections: uniqueSections.join(','),
            sections_url: `${window.location.pathname}${window.location.search}`,
          }
        : {}),
    }),
  });
  const appliedBridge = teamPricingBridgeCode(hoodieQuantity(after));
  if (appliedBridge && !applicableDiscountCode(after, appliedBridge) && cartTotals(after).final > quoteTeamPricing(hoodieQuantity(after)).totalCents)
    throw new Error('team_pricing_bridge_rejected');
  render(after);
  if (window.themeVariables?.settings?.pageType === 'cart' && !state.checkoutRequested) {
    window.location.reload();
    return after;
  }
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'nwp:team-pricing', cart: after },
    }),
  );
  return after;
}

function schedule(candidate) {
  state.queue = state.queue
    .catch(() => null)
    .then(() => reconcile(candidate))
    .catch((error) => {
      console.error('[NWP team pricing]', error);
      announceFailure();
      return null;
    });
  return state.queue;
}

function quantityInput(event) {
  if (!event.target.matches?.('[name="quantity"]')) return;
  if (state.cart) render(state.cart);
}

async function guardedCheckout(event) {
  const control = event.type === 'submit' ? event.submitter : event.target.closest?.('[name="checkout"], a[href*="/checkout"]');
  if (!control?.matches?.('[name="checkout"], a[href*="/checkout"]')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (state.checkoutRequested) return;
  state.checkoutRequested = true;
  control.setAttribute('aria-busy', 'true');
  try {
    const promises = [];
    document.dispatchEvent(new CustomEvent('nwp:cart:await', { detail: { promises } }));
    await Promise.all(promises);
    if (document.querySelector('[data-cart-update-error]')) throw new Error('cart_change_failed');
    const reconciled = await schedule();
    if (!reconciled) throw new Error('team_pricing_unverified');
    const current = await cartRequest('cart.js');
    if (JSON.stringify(current.items) !== JSON.stringify(reconciled.items)) throw new Error('cart_changed');
    const quantity = hoodieQuantity(current);
    if (quantity && cartTotals(current).final > quoteTeamPricing(quantity).totalCents) throw new Error('team_pricing_unverified');
    const verifiedCart = await cartRequest('cart.js');
    if (JSON.stringify(verifiedCart.items) !== JSON.stringify(current.items)) throw new Error('cart_changed');
    window.location.assign(route('checkout'));
  } catch {
    announceFailure();
  } finally {
    state.checkoutRequested = false;
    control.removeAttribute('aria-busy');
  }
}

document.addEventListener('cart:change', (event) => {
  if (event.detail?.baseEvent === 'nwp:team-pricing') return;
  schedule(event.detail?.cart);
});
document.addEventListener('cart:refresh', () => schedule());
document.addEventListener('cart-drawer:refreshed', () => {
  if (state.cart) render(state.cart);
});
document.addEventListener('input', quantityInput);
document.addEventListener('change', quantityInput);
document.addEventListener('click', guardedCheckout, true);
document.addEventListener('submit', guardedCheckout, true);

schedule();
